import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  searchIndex,
  groupByCategory,
  getCategoryLabel,
  type SearchItem,
  type SearchCategory,
} from "@/config/search.config";
import { FileText, Castle, Calendar, MapPin, Clock } from "lucide-react";

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const categoryIcons: Record<SearchCategory, React.ElementType> = {
  pages: FileText,
  castelli: Castle,
  attivita: Calendar,
};

const SearchDialog: React.FC<SearchDialogProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchItem[]>([]);

  // Esegui la ricerca quando cambia la query
  useEffect(() => {
    if (query.length >= 2) {
      const searchResults = searchIndex(query, 15);
      setResults(searchResults);
    } else {
      setResults([]);
    }
  }, [query]);

  // Reset quando si chiude
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  // Gestisci la selezione di un risultato
  const handleSelect = useCallback(
    (item: SearchItem) => {
      onOpenChange(false);

      // Naviga alla pagina appropriata con i parametri corretti
      if (item.category === "castelli") {
        // Passa il titolo completo per la ricerca
        navigate(`/castelli?search=${encodeURIComponent(item.title)}`);
      } else if (item.category === "attivita") {
        // Passa il titolo dell'attività per scrollare direttamente ad essa
        navigate(`/attivita?activity=${encodeURIComponent(item.title)}`);
      } else {
        navigate(item.path);
      }
    },
    [navigate, onOpenChange],
  );

  // Raggruppa i risultati per categoria
  const groupedResults = groupByCategory(results);
  const hasResults = results.length > 0;
  const categories = (["pages", "castelli", "attivita"] as SearchCategory[]).filter(
    (cat) => groupedResults[cat].length > 0,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 max-w-lg">
        <Command
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
          shouldFilter={false}
        >
          <CommandInput placeholder="Cerca pagine, castelli, attività..." value={query} onValueChange={setQuery} />
          <CommandList className="max-h-[400px]">
            {query.length >= 2 && !hasResults && (
              <CommandEmpty>
                <div className="py-6 text-center">
                  <p className="text-sm text-muted-foreground">Nessun risultato per "{query}"</p>
                  <p className="text-xs text-muted-foreground mt-1">Prova con termini diversi</p>
                </div>
              </CommandEmpty>
            )}

            {query.length < 2 && (
              <div className="py-6 text-center">
                <p className="text-sm text-muted-foreground">Digita almeno 2 caratteri per cercare</p>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <span className="text-xs bg-muted px-2 py-1 rounded">2026</span>
                  <span className="text-xs bg-muted px-2 py-1 rounded">escursioni</span>
                  <span className="text-xs bg-muted px-2 py-1 rounded">tesseramento</span>
                  <span className="text-xs bg-muted px-2 py-1 rounded">castello</span>
                </div>
              </div>
            )}

            {categories.map((category, index) => (
              <React.Fragment key={category}>
                {index > 0 && <CommandSeparator />}
                <CommandGroup heading={getCategoryLabel(category)}>
                  {groupedResults[category].map((item) => {
                    const Icon = item.icon || categoryIcons[category];
                    return (
                      <CommandItem
                        key={item.id}
                        value={`${item.id}-${item.title}`}
                        onSelect={() => handleSelect(item)}
                        className="cursor-pointer py-3 group data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary"
                      >
                        <Icon className="mr-3 h-4 w-4 text-muted-foreground group-data-[selected=true]:text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{item.title}</div>
                          <div className="text-xs text-muted-foreground group-data-[selected=true]:text-primary/70 line-clamp-1">
                            {item.description}
                          </div>
                          {item.meta && (
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground group-data-[selected=true]:text-primary/70">
                              {item.meta.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {item.meta.location}
                                </span>
                              )}
                              {item.meta.date && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {item.meta.date}
                                </span>
                              )}
                              {item.meta.type && (
                                <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded text-[10px]">
                                  {item.meta.type}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </React.Fragment>
            ))}
          </CommandList>

          {/* Footer con shortcut */}
          <div className="border-t px-3 py-2 text-xs text-muted-foreground flex items-center justify-between bg-muted/30">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-background rounded text-[10px] font-mono border">↑↓</kbd>
                naviga
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-background rounded text-[10px] font-mono border">↵</kbd>
                seleziona
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-background rounded text-[10px] font-mono border">esc</kbd>
                chiudi
              </span>
            </div>
            <div>
              {hasResults && (
                <span>
                  {results.length} risultat{results.length === 1 ? "o" : "i"}
                </span>
              )}
            </div>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
